/**
 * Mutation hook to upsert user_business_context patches.
 *
 * Exposes both an immediate `mutate` and `mutateDebounced(patch, 800)` —
 * debounce key is the JSON shape of the patch keys, so independent fields
 * coalesce independently and rapid edits to the same field collapse.
 */

import { useCallback, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSupabaseClient } from "@/lib/supabase";
import {
  businessContextQueryKey,
  useInvalidateBusinessContext,
} from "@/hooks/useBusinessContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useProfileId } from "./useProfileId";
import {
  updateBusinessContext,
  updateProfileIdentity,
  type UBCRow,
  type UBCUpdate,
} from "../api/businessContext";
import { toast } from "@/lib/toast";

export type WizardPatch = UBCUpdate & {
  display_name?: string;
  honorific_prefix?: "" | "Dr." | "Dra." | null;
};

export interface UseUpdateBusinessContextResult {
  mutate: (patch: WizardPatch) => Promise<UBCRow | null>;
  mutateDebounced: (patch: WizardPatch, delayMs?: number) => void;
  isPending: boolean;
  isError: boolean;
  reset: () => void;
}

const DEFAULT_DEBOUNCE_MS = 800;

export function useUpdateBusinessContext(): UseUpdateBusinessContextResult {
  const { client } = useSupabaseClient();
  const { user } = useCurrentUser();
  const { profileId } = useProfileId();
  const qc = useQueryClient();
  const invalidate = useInvalidateBusinessContext();

  const mutation = useMutation<UBCRow | null, Error, WizardPatch>({
    mutationFn: async (patch) => {
      if (!profileId) throw new Error("Profile not ready");
      const { display_name, honorific_prefix, ...ubcPatch } = patch;
      let row: UBCRow | null = null;
      if (Object.keys(ubcPatch).length > 0) {
        row = await updateBusinessContext({ client, userId: profileId, patch: ubcPatch });
      }
      if (display_name !== undefined || honorific_prefix !== undefined) {
        await updateProfileIdentity(client, profileId, {
          display_name,
          honorific_prefix: honorific_prefix === "" ? null : honorific_prefix,
        });
      }
      return row;
    },
    onSuccess: async (row) => {
      const key = businessContextQueryKey(user?.id);
      qc.setQueryData(key, (prev: unknown) => {
        if (prev && typeof prev === "object" && row) {
          return { ...(prev as Record<string, unknown>), ...row };
        }
        return prev;
      });
      await invalidate();
    },
    onError: (err) => {
      console.error("[useUpdateBusinessContext] save failed:", err);
      toast.error("Erro ao salvar. Tente novamente.");
    },
  });

  // Debounce buckets keyed by sorted patch keys so independent fields
  // (e.g. niche vs years_active) flush on their own timers.
  const buffersRef = useRef<Map<string, { patch: WizardPatch; timer: ReturnType<typeof setTimeout> }>>(
    new Map(),
  );

  useEffect(() => {
    const buffers = buffersRef.current;
    return () => {
      for (const { timer } of buffers.values()) clearTimeout(timer);
      buffers.clear();
    };
  }, []);

  const mutateDebounced = useCallback(
    (patch: WizardPatch, delayMs: number = DEFAULT_DEBOUNCE_MS) => {
      const buffers = buffersRef.current;
      const key = Object.keys(patch).sort().join("|");
      const existing = buffers.get(key);
      const merged: WizardPatch = existing ? { ...existing.patch, ...patch } : patch;
      if (existing) clearTimeout(existing.timer);
      const timer = setTimeout(() => {
        buffers.delete(key);
        mutation.mutate(merged);
      }, delayMs);
      buffers.set(key, { patch: merged, timer });
    },
    [mutation],
  );

  const mutate = useCallback(
    (patch: WizardPatch) => mutation.mutateAsync(patch),
    [mutation],
  );

  return {
    mutate,
    mutateDebounced,
    isPending: mutation.isPending,
    isError: mutation.isError,
    reset: mutation.reset,
  };
}
